// pages/purchase-entry/UploadStep.tsx

import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, X, Loader2, Check, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useInvoiceEntryStore } from '@/stores/invoiceEntryStore';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import RulesDialog from './RulesDialog';

const API_URL = import.meta.env.VITE_API_URL || '';

const ACCEPTED_TYPES = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/webp': ['.webp'],
};

type UploadStatus = 'pending' | 'uploading' | 'uploaded' | 'failed';

interface FileWithStatus {
  file: File;
  status: UploadStatus;
  progress: number;
}

export function UploadStep() {
  const {
    company,
    transactionType,
    setCurrentStep,
    setBatchId,
    setTotalCount,
    setError,
    error
  } = useInvoiceEntryStore();

  const [files, setFiles] = useState<FileWithStatus[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const newFiles = acceptedFiles.map(file => ({
        file,
        status: 'pending' as UploadStatus,
        progress: 0,
      }));
      setFiles(prev => [...prev, ...newFiles]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    multiple: true,
  });

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleStartExtraction = async () => {
    if (files.length === 0 || !company) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const formData = new FormData();

      // Append all files
      files.forEach(({ file }) => {
        formData.append('files', file);
      });
      formData.append('company', company);
      formData.append('transaction_type', transactionType);

      // Update all files to uploading status
      setFiles(prev => prev.map(f => ({ ...f, status: 'uploading' as UploadStatus })));

      const response = await fetch(`${API_URL}/api/invoice-entry/batch-extract`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to start batch extraction');
      }

      const data = await response.json();

      // Update files to uploaded status
      setFiles(prev => prev.map(f => ({
        ...f,
        status: 'uploaded' as UploadStatus,
        progress: 100
      })));

      // Store batch data and navigate
      setBatchId(data.batch_id);
      setTotalCount(data.total_count);

      toast({
        title: 'Upload Complete',
        description: `Successfully uploaded ${data.total_count} invoice(s) for processing`,
      });

      setCurrentStep('extraction');
    } catch (err) {
      console.error('Batch extraction error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to start batch extraction';

      // Update files to failed status
      setFiles(prev => prev.map(f => ({ ...f, status: 'failed' as UploadStatus })));

      setError(errorMessage);
      toast({
        title: 'Upload Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isReady = files.length > 0 && company && !error;
  const hasFiles = files.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button variant="outline" onClick={() => setShowRules(true)}>
          Custom Rules
        </Button>
      </div>

      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={cn(
          'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
          isDragActive && 'border-primary bg-primary/5',
          !isDragActive && 'border-muted-foreground/25 hover:border-primary/50',
          hasFiles && 'border-green-500 bg-green-50'
        )}
      >
        <input {...getInputProps()} />

        {!hasFiles ? (
          <div className="space-y-2">
            <Upload className="h-10 w-10 mx-auto text-muted-foreground" />
            <div>
              <p className="font-medium">
                {isDragActive ? 'Drop the files here' : 'Drag & drop purchase invoices'}
              </p>
              <p className="text-sm text-muted-foreground">
                or click to browse (PDF, PNG, JPG, WEBP)
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <Upload className="h-10 w-10 mx-auto text-green-600" />
            <p className="font-medium text-green-700">
              {files.length} file{files.length > 1 ? 's' : ''} selected
            </p>
            <p className="text-sm text-muted-foreground">
              Drop more files or click to add more
            </p>
          </div>
        )}
      </div>

      {/* File List */}
      {hasFiles && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium">Selected Files</h3>
          <div className="space-y-2">
            {files.map((fileItem, index) => (
              <div
                key={`${fileItem.file.name}-${index}`}
                className="flex items-center gap-3 px-3 py-2 border rounded-lg bg-card"
              >
                <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />

                <div className="flex-1 min-w-0 flex items-center gap-2">
                  <p className="font-medium text-sm truncate">
                    {fileItem.file.name}
                  </p>
                  <p className="text-xs text-muted-foreground whitespace-nowrap">
                    ({formatFileSize(fileItem.file.size)})
                  </p>

                  {/* Status indicators - inline */}
                  {fileItem.status === 'uploading' && (
                    <div className="flex items-center gap-2 ml-auto">
                      <Progress value={fileItem.progress} className="h-1.5 w-24" />
                      <p className="text-xs text-muted-foreground whitespace-nowrap">
                        {fileItem.progress}%
                      </p>
                    </div>
                  )}

                  {fileItem.status === 'uploaded' && (
                    <div className="flex items-center gap-1 ml-auto">
                      <Check className="h-3 w-3 text-green-600" />
                      <p className="text-xs text-green-600 whitespace-nowrap">Uploaded</p>
                    </div>
                  )}

                  {fileItem.status === 'failed' && (
                    <div className="flex items-center gap-1 ml-auto">
                      <AlertCircle className="h-3 w-3 text-red-600" />
                      <p className="text-xs text-red-600 whitespace-nowrap">Failed</p>
                    </div>
                  )}
                </div>

                {/* Remove button (only when not uploading) */}
                {fileItem.status !== 'uploading' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFile(index)}
                    disabled={isSubmitting}
                    className="h-6 w-6 p-0"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action button */}
      <div className="flex justify-end">
        <Button
          onClick={handleStartExtraction}
          disabled={!isReady || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Uploading...
            </>
          ) : (
            `Extract ${files.length > 1 ? 'All Invoices' : 'Invoice Data'}`
          )}
        </Button>
      </div>

      <RulesDialog
        open={showRules}
        onOpenChange={setShowRules}
        transactionType={transactionType}
        company={company || ''}
      />
    </div>
  );
}
